import { useParams } from "@tanstack/react-router";
import { SearchIcon, ShieldCheckIcon } from "lucide-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { useOrgProfile } from "../../state/orgProfile";
import { useFrameworks, type FrameworkList } from "../../state/governance";
import { ListSkeleton } from "./LoadingSkeletons";
import { RegimeCard } from "./RegimeCard";
import { regimeMatchesSearch, regimeShelves } from "./regimeShelves";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { HairlineArt } from "../ui/hairline-art";
import { Globe } from "../hairline/figures";

interface Props {
  organizationId: string | null;
  search: string;
}

/** Tenant changes reset local requests and confirmed changes together. */
export function FrameworksSection(props: Props) {
  return <RegimeCatalogue key={props.organizationId ?? "no-org"} {...props} />;
}

function RegimeCatalogue({ organizationId, search }: Props) {
  const { snapshot, isPending, refresh } = useFrameworks(organizationId);
  const [localSearch, setLocalSearch] = useState("");
  const { canManage } = useOrgProfile(organizationId);
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });
  const latestSnapshot = useRef(snapshot);
  latestSnapshot.current = snapshot;
  const inFlight = useRef(new Set<string>());
  const scrollAnchor = useRef<{
    element: HTMLElement;
    scroller: HTMLElement;
    top: number;
  } | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [confirmed, setConfirmed] = useState<
    Record<string, { enabled: boolean; baseline: FrameworkList | null }>
  >({});

  useLayoutEffect(() => {
    const anchor = scrollAnchor.current;
    scrollAnchor.current = null;
    if (!anchor?.element.isConnected) return;
    anchor.scroller.scrollTop += anchor.element.getBoundingClientRect().top - anchor.top;
  });

  const changeEnabled = useCallback(
    async (id: string, enabled: boolean, button?: HTMLButtonElement) => {
      if (!organizationId || inFlight.current.has(id)) return;
      inFlight.current.add(id);
      setBusyIds(new Set(inFlight.current));
      setErrors((current) => ({ ...current, [id]: null }));
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/frameworks", {
            organizationId,
            frameworkId: id,
          }),
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ enabled }),
          },
        );
        if (!response.ok) {
          setErrors((current) => ({
            ...current,
            [id]:
              response.status === 403
                ? "Only organization admins can change regimes."
                : "The regime could not be updated. Try again.",
          }));
          return;
        }
        // Acknowledged changes paint immediately; the next server snapshot is authoritative.
        // Capture after the request, so scrolling while it was pending is respected.
        const scroller = button?.closest<HTMLElement>(".regulation-page-scroll-fade");
        if (button?.isConnected && scroller) {
          // Removing an Added copy disconnects the button. Its catalogue shelf survives
          // and provides a stable anchor when an Added grid row (or the shelf) disappears.
          const element = !enabled && button.closest('[data-regime-shelf="added"]')
            ? scroller.querySelector<HTMLElement>('[data-regime-shelf^="domain:"]')
            : button;
          if (element) scrollAnchor.current = { element, scroller, top: element.getBoundingClientRect().top };
        }
        const baseline = latestSnapshot.current;
        setConfirmed((current) => ({ ...current, [id]: { enabled, baseline } }));
        refresh();
      } catch {
        setErrors((current) => ({
          ...current,
          [id]: "The regime could not be updated. Try again.",
        }));
      } finally {
        inFlight.current.delete(id);
        setBusyIds(new Set(inFlight.current));
      }
    },
    [organizationId, refresh],
  );

  // The shell's shared query (always empty today) and the page's own box narrow together.
  const query = `${search} ${localSearch}`.trim();
  const catalogue = snapshot?.frameworks ?? [];
  const frameworks = catalogue
    .map((framework) => {
      const change = confirmed[framework.id];
      return change?.baseline === snapshot ? { ...framework, enabled: change.enabled } : framework;
    })
    .filter((framework) => regimeMatchesSearch(framework, query));

  const shelves = regimeShelves(frameworks);

  const searchBox =
    catalogue.length > 0 ? (
      <div className="mt-6 max-w-md">
        <InputGroup className="h-8">
          <InputGroupAddon className="text-muted-foreground">
            <SearchIcon className="size-3.5" />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            value={localSearch}
            onChange={(event) => setLocalSearch(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && localSearch) {
                event.preventDefault();
                setLocalSearch("");
              }
            }}
            placeholder="Search regimes, issuers or jurisdictions"
            aria-label="Search regimes"
            className="text-sm"
          />
        </InputGroup>
      </div>
    ) : null;

  if (frameworks.length === 0) {
    if (isPending && snapshot === null) {
      return <ListSkeleton className="mt-6" rows={4} />;
    }
    return (
      <>
      {searchBox}
      <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-border/60 px-6 py-16 text-center">
        {query.length > 0 ? (
          <ShieldCheckIcon className="size-5 text-muted-foreground/60" />
        ) : (
          <HairlineArt figure={Globe} className="w-48 sm:w-56" />
        )}
        <p className="mt-3 text-sm font-medium text-foreground">
          {query.length > 0 ? "No regimes match that search" : "No regimes in the catalogue"}
        </p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {query.length > 0
            ? "Try a regime's name, its issuer or a jurisdiction."
            : "Regimes describe the controls an auditor will ask about. Once the catalogue is loaded you can add the ones that apply to you."}
        </p>
      </div>
      </>
    );
  }

  return (
    <>
    {searchBox}
    <div className="mt-6 space-y-8">
      {shelves.map((shelf) =>
        shelf.items.length > 0 ? (
          <section key={shelf.key} data-regime-shelf={shelf.key}>
            <h2 className="mb-3 flex items-baseline gap-2 text-sm font-semibold text-foreground">
              {shelf.title}
              <span className="text-xs font-normal tabular-nums text-muted-foreground">
                {shelf.items.length}
              </span>
            </h2>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {shelf.items.map((framework) => (
                <RegimeCard
                  key={framework.id}
                  framework={framework}
                  orgSegment={orgSegment}
                  canManage={canManage}
                  busy={busyIds.has(framework.id)}
                  error={errors[framework.id] ?? null}
                  onChange={changeEnabled}
                />
              ))}
            </ul>
          </section>
        ) : null,
      )}
      {!canManage ? (
        <p className="text-xs text-muted-foreground">Only organization admins can add regimes.</p>
      ) : null}
    </div>
    </>
  );
}
